import { AuthUserContextProvider, useAuthUser } from "./AuthUserContext";
import { TurnstileProvider } from "./TurnstileContext";
import { Divider } from "./base-ui";
import {
  EmailAuth,
  EmailAuthProps,
  ForgottenPassword,
  MagicLink,
  MfaChallenge,
  SocialAuth,
  UpdatePassword,
  VerifyOtp,
} from "./interfaces";
import { SSO } from "./interfaces/SSO";
import { AUTH_VIEWS, Auth as AuthProps } from "./types";
import { cn } from "@/lib/utils";
import { assertNever } from "@/packages/ts-utils";
import { deepClone } from "@/utils/helpers";
import { en, I18nVariables, merge } from "@supabase/auth-ui-shared";
import { useSearchParams } from "next/navigation";
import React, { useEffect, useState, type JSX } from "react";

function Auth({
  supabaseClient,
  providers,
  providerScopes,
  queryParams,
  view = "sign_in",
  setAuthView,
  redirectTo,
  onlyThirdPartyProviders = false,
  showLinks = true,
  localization = { variables: {} },
  otpType = "email",
  skipBeforeUserCreateHookKey,
  passwordLoginMode = "turnstile",
  //passwordLimit,
  title,
  children,
}: AuthProps): JSX.Element | null {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const i18n: I18nVariables = merge(
    deepClone(en),
    localization.variables ?? {}
  );
  const authView = view;
  const searchParams = useSearchParams();

  // Sign-up hides the email form behind a "Continue with Email" button.
  // Invite links arrive with a prefilled email, so those skip straight to the form.
  const [isEmailFormRevealed, setIsEmailFormRevealed] = useState(false);
  const showEmailForm = isEmailFormRevealed || !!searchParams.get("email");

  useEffect(() => {
    setIsEmailFormRevealed(false);
  }, [authView]);

  const isSignUpView = authView === "sign_up";

  const renderContainer = ({
    children,
    bottomLink,
  }: {
    children: React.ReactNode;
    bottomLink?: React.ReactNode;
  }) => (
    <TurnstileProvider passwordLoginMode={passwordLoginMode}>
      <div className="flex w-full flex-col items-center gap-9">
        {title}
        <div className="flex w-full flex-col gap-6">{children}</div>
        {showLinks && bottomLink}
      </div>
    </TurnstileProvider>
  );

  const renderAccountSwitchLink = () => (
    <p
      className={cn(
        "text-center text-sm",
        isSignUpView ? "text-blue-500" : "text-content-primary"
      )}
    >
      {isSignUpView ? "Already have an account?" : "Don't have an account?"}{" "}
      <a
        href={isSignUpView ? "auth?view=sign_in" : "auth?view=sign_up"}
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          setAuthView(isSignUpView ? AUTH_VIEWS.SIGN_IN : AUTH_VIEWS.SIGN_UP);
        }}
        className={cn(
          "text-blue-500 hover:text-blue-600",
          isSignUpView && "underline underline-offset-2"
        )}
      >
        {isSignUpView ? "Log in" : "Sign up"}
      </a>
    </p>
  );

  useEffect(() => {
    const { data: authListener } = supabaseClient.auth.onAuthStateChange(
      (event) => {
        if (event === "PASSWORD_RECOVERY") {
          setAuthView("update_password");
        } else if (
          event === "USER_UPDATED" &&
          // When loading the update_password view, this event always comes in but we want to stay at the update_password page
          view !== "update_password"
        ) {
          setAuthView("sign_in");
        }
      }
    );

    return () => authListener.subscription.unsubscribe();
  }, [view, setAuthView, supabaseClient.auth]);

  const emailAuthProps: Omit<EmailAuthProps, "authView" | "id"> = {
    supabaseClient,
    setAuthView,
    showLinks,
    i18n,
    //passwordLimit,
  };

  const socialAuthProps = {
    supabaseClient,
    providers,
    providerScopes,
    queryParams,
    redirectTo,
    setAuthView,
  };

  switch (authView) {
    case AUTH_VIEWS.SIGN_IN:
      return renderContainer({
        children: (
          <>
            {!onlyThirdPartyProviders && (
              <EmailAuth {...emailAuthProps} authView={AUTH_VIEWS.SIGN_IN} />
            )}
            {providers && providers.length > 0 && (
              <>
                {!onlyThirdPartyProviders && <Divider />}
                <SocialAuth {...socialAuthProps} view={AUTH_VIEWS.SIGN_IN} />
              </>
            )}
          </>
        ),
        bottomLink: renderAccountSwitchLink(),
      });
    case AUTH_VIEWS.SIGN_UP:
      return renderContainer({
        children: (
          <>
            <SocialAuth
              {...socialAuthProps}
              view={AUTH_VIEWS.SIGN_UP}
              onEmailPress={
                onlyThirdPartyProviders || showEmailForm
                  ? undefined
                  : () => setIsEmailFormRevealed(true)
              }
            />
            {!onlyThirdPartyProviders && showEmailForm && (
              <EmailAuth {...emailAuthProps} authView={AUTH_VIEWS.SIGN_UP}>
                {children}
              </EmailAuth>
            )}
          </>
        ),
        bottomLink: renderAccountSwitchLink(),
      });
    case AUTH_VIEWS.FORGOTTEN_PASSWORD:
      return renderContainer({
        children: (
          <ForgottenPassword
            supabaseClient={supabaseClient}
            setAuthView={setAuthView}
            showLinks={showLinks}
            i18n={i18n}
            redirectTo={redirectTo}
          />
        ),
      });

    case AUTH_VIEWS.MAGIC_LINK:
      return renderContainer({
        children: (
          <>
            <MagicLink
              supabaseClient={supabaseClient}
              setAuthView={setAuthView}
              redirectTo={redirectTo}
              showLinks={showLinks}
              i18n={i18n}
              skipBeforeUserCreateHookKey={skipBeforeUserCreateHookKey}
            />
            {providers && providers.length > 0 && (
              <>
                <Divider />
                <SocialAuth {...socialAuthProps} view={AUTH_VIEWS.MAGIC_LINK} />
              </>
            )}
          </>
        ),
      });

    case AUTH_VIEWS.UPDATE_PASSWORD:
      return (
        <TurnstileProvider>
          <div className="flex w-full flex-col items-center gap-9">
            {title}
            <UpdatePassword
              i18n={i18n}
              //passwordLimit={passwordLimit}
            />
          </div>
        </TurnstileProvider>
      );
    case AUTH_VIEWS.VERIFY_OTP:
      return (
        <div className="flex w-full flex-col items-center gap-9">
          {title}
          <VerifyOtp
            supabaseClient={supabaseClient}
            otpType={otpType}
            i18n={i18n}
          />
        </div>
      );

    case AUTH_VIEWS.MFA_CHALLENGE:
      return renderContainer({
        children: <MfaChallenge supabaseClient={supabaseClient} />,
      });
    case AUTH_VIEWS.SSO:
      return renderContainer({
        children: (
          <SSO
            supabaseClient={supabaseClient}
            setAuthView={setAuthView}
            redirectTo={redirectTo}
            showLinks={showLinks}
            i18n={i18n}
          />
        ),
      });
    default:
      return assertNever(authView);
  }
}

Auth.ForgottenPassword = ForgottenPassword;
Auth.UpdatePassword = UpdatePassword;
Auth.MagicLink = MagicLink;
Auth.UserContextProvider = AuthUserContextProvider;
Auth.useUser = useAuthUser;

export default Auth;
